import {
  ActionIcon,
  Alert,
  Anchor,
  Button,
  CloseButton,
  Code,
  Group,
  Loader,
  Menu,
  Modal,
  Paper,
  Stack,
  Table,
  Text,
  Textarea,
  TextInput,
  Title,
} from "@mantine/core";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import {
  ApiError,
  api,
  type CascadeUsageResponse,
  type ClientResponse,
} from "../lib/api";

interface ClientWithLastUpload {
  client_id: string;
  name: string;
  phone_e164: string | null;
  email: string | null;
  last_upload_at: string | null;
}

/**
 * Clients tab: persistent CRM. Lets the accountant build up their client
 * roster once and reuse them across campaigns/runs (vs. recreating one
 * per invite via QuickInviteScreen).
 */
export function ClientsScreen() {
  const [clients, setClients] = useState<ClientResponse[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [openCreate, setOpenCreate] = useState(false);
  const [openBulk, setOpenBulk] = useState(false);
  const [editing, setEditing] = useState<ClientResponse | null>(null);
  const [clientsRich, setClientsRich] = useState<ClientWithLastUpload[] | null>(null);
  const [search, setSearch] = useState("");
  const [purging, setPurging] = useState<ClientWithLastUpload | null>(null);

  const refresh = useCallback(async () => {
    try {
      const [base, rich] = await Promise.all([
        api.clientsList(),
        api.clientsListWithLastUpload().catch(() => ({ clients: [] })),
      ]);
      setClients(base.clients);
      setClientsRich(rich.clients);
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : "שגיאה בטעינה");
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const visibleRows = useMemo(() => {
    if (clientsRich === null) return null;
    const q = search.trim().toLowerCase();
    if (!q) return clientsRich;
    return clientsRich.filter((c) => c.name.toLowerCase().includes(q));
  }, [clientsRich, search]);

  const handleArchive = async (id: string) => {
    if (!window.confirm("להעביר את הלקוח לארכיון? הקבצים שלו נשמרים.")) return;
    try {
      await api.clientDelete(id);
      await refresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : "שגיאה בארכוב");
    }
  };

  return (
    <Stack>
      <Group justify="space-between">
        <Title order={2}>לקוחות</Title>
        <Group>
          <Button variant="light" onClick={() => setOpenBulk(true)}>
            ייבוא מ־CSV
          </Button>
          <Button onClick={() => setOpenCreate(true)}>הוסף לקוח</Button>
        </Group>
      </Group>
      <Text c="dimmed">רשימת הלקוחות שלך. הוסף, ערוך, או הסתר.</Text>

      {error && <Alert color="red">{error}</Alert>}

      <TextInput
        placeholder="חיפוש לפי שם…"
        value={search}
        onChange={(e) => setSearch(e.currentTarget.value)}
        rightSection={
          search ? (
            <CloseButton
              size="sm"
              aria-label="נקה חיפוש"
              onClick={() => setSearch("")}
            />
          ) : null
        }
        dir="rtl"
      />

      {visibleRows === null ? (
        <Loader size="sm" />
      ) : (clientsRich?.length ?? 0) === 0 ? (
        <Paper p="xl" withBorder>
          <Stack align="center">
            <Text c="dimmed">עדיין אין לקוחות. הוסף את הראשון כדי להתחיל.</Text>
            <Button onClick={() => setOpenCreate(true)}>הוסף לקוח</Button>
          </Stack>
        </Paper>
      ) : visibleRows.length === 0 ? (
        <Paper p="xl" withBorder>
          <Text c="dimmed" ta="center">
            לא נמצאו לקוחות התואמים ל־"{search}".
          </Text>
        </Paper>
      ) : (
        <Paper withBorder>
          <Table dir="rtl" striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>שם</Table.Th>
                <Table.Th>טלפון</Table.Th>
                <Table.Th>העלאה אחרונה</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {visibleRows.map((c) => (
                <Table.Tr key={c.client_id}>
                  <Table.Td>
                    <Anchor component={Link} to={`/clients/${c.client_id}`}>
                      {c.name}
                    </Anchor>
                  </Table.Td>
                  <Table.Td dir="ltr">{c.phone_e164 ?? "—"}</Table.Td>
                  <Table.Td>
                    {c.last_upload_at ? (
                      <Text size="sm">
                        {new Date(c.last_upload_at).toLocaleDateString("he-IL")}
                      </Text>
                    ) : (
                      <Text size="sm" c="dimmed">
                        טרם הועלה
                      </Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Group gap={6}>
                      <ActionIcon
                        variant="subtle"
                        aria-label="ערוך"
                        title="ערוך"
                        onClick={() =>
                          setEditing({
                            client_id: c.client_id,
                            name: c.name,
                            phone_e164: c.phone_e164,
                            email: c.email,
                          })
                        }
                      >
                        ✎
                      </ActionIcon>
                      <Menu shadow="md" position="bottom-end">
                        <Menu.Target>
                          <ActionIcon
                            variant="subtle"
                            color="red"
                            aria-label="מחק או ארכב"
                            title="מחק או ארכב"
                          >
                            🗑
                          </ActionIcon>
                        </Menu.Target>
                        <Menu.Dropdown>
                          <Menu.Item onClick={() => handleArchive(c.client_id)}>
                            העבר לארכיון (שמור היסטוריה)
                          </Menu.Item>
                          <Menu.Item
                            color="red"
                            onClick={() => setPurging(c)}
                          >
                            מחק לצמיתות (כולל קבצים)
                          </Menu.Item>
                        </Menu.Dropdown>
                      </Menu>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Paper>
      )}

      <ClientFormModal
        opened={openCreate}
        onClose={() => setOpenCreate(false)}
        onSaved={async () => {
          setOpenCreate(false);
          await refresh();
        }}
      />
      <ClientFormModal
        opened={editing !== null}
        client={editing ?? undefined}
        onClose={() => setEditing(null)}
        onSaved={async () => {
          setEditing(null);
          await refresh();
        }}
      />
      <BulkImportModal
        opened={openBulk}
        onClose={() => setOpenBulk(false)}
        onDone={async () => {
          setOpenBulk(false);
          await refresh();
        }}
      />
      {purging && (
        <ClientPurgeModal
          client={purging}
          onClose={() => setPurging(null)}
          onDeleted={async () => {
            setPurging(null);
            await refresh();
          }}
        />
      )}
    </Stack>
  );
}

function ClientPurgeModal({
  client,
  onClose,
  onDeleted,
}: {
  client: ClientWithLastUpload;
  onClose: () => void;
  onDeleted: () => void | Promise<void>;
}) {
  const [usage, setUsage] = useState<CascadeUsageResponse | null>(null);
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .clientUsage(client.client_id)
      .then(setUsage)
      .catch((err) =>
        setError(err instanceof ApiError ? err.detail : "טעינת נתונים נכשלה"),
      );
  }, [client.client_id]);

  const expected = `מחק ${client.name}`;
  const ready = confirmText.trim() === expected.trim();
  const hasData =
    usage !== null &&
    (usage.invites > 0 || usage.files > 0);

  const doDelete = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.clientDeletePermanent(client.client_id);
      await onDeleted();
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : "מחיקה נכשלה");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal opened onClose={onClose} title={`מחיקה לצמיתות — ${client.name}`} size="md">
      <Stack>
        <Alert color="red" title="פעולה בלתי-הפיכה">
          מחיקה לצמיתות תסיר את הלקוח, כל ההזמנות שלו, וכל הקבצים שהעלה
          (כולל המוצפנים באחסון). אין דרך לשחזר. אם אתה רוצה רק להסתיר
          את הלקוח מהרשימה, בחר במקום זאת "העבר לארכיון".
        </Alert>
        {usage === null && !error ? (
          <Loader size="sm" />
        ) : usage !== null ? (
          <Paper p="sm" withBorder>
            <Stack gap={4}>
              <Text size="sm" fw={500}>השפעה צפויה:</Text>
              <Text size="sm">• {usage.invites} הזמנות ב-{usage.runs} קמפיינים</Text>
              <Text size="sm">
                • {usage.files} קבצים מוצפנים
                ({(usage.bytes_total / 1024 / 1024).toFixed(1)} MB)
              </Text>
            </Stack>
          </Paper>
        ) : null}
        {hasData && (
          <TextInput
            label={`הקלד "${expected}" כדי לאשר`}
            value={confirmText}
            onChange={(e) => setConfirmText(e.currentTarget.value)}
            placeholder={expected}
            dir="rtl"
            autoFocus
          />
        )}
        {error && <Alert color="red">{error}</Alert>}
        <Group justify="flex-end">
          <Button variant="subtle" onClick={onClose}>ביטול</Button>
          <Button
            color="red"
            onClick={doDelete}
            loading={busy}
            disabled={hasData && !ready}
          >
            מחק לצמיתות
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

/**
 * BulkImportModal — paste-area for CSV-style `name,phone` rows. Israeli
 * accountants live in spreadsheets; the dashboard would be unusable
 * without this for anyone migrating from Excel.
 *
 * Parses client-side, ships parsed list as one round-trip to
 * /v1/clients/bulk. Shows per-row failures inline.
 */
function BulkImportModal({
  opened,
  onClose,
  onDone,
}: {
  opened: boolean;
  onClose: () => void;
  onDone: () => void | Promise<void>;
}) {
  const [pasted, setPasted] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{
    created: number;
    failures: { name: string; reason: string }[];
  } | null>(null);

  const parsed = useMemo(() => {
    const out: { name: string; phone_e164: string | null; email: string | null }[] = [];
    for (const raw of pasted.split(/\r?\n/)) {
      const line = raw.trim();
      if (!line) continue;
      // Tolerate tab as a separator too — Excel paste hands tabs by default.
      const cols = line.split(/[,\t]/).map((s) => s.trim());
      const name = cols[0];
      const phone = cols[1] || null;
      const email = cols[2] || null;
      if (!name) continue;
      out.push({ name, phone_e164: phone, email });
    }
    return out;
  }, [pasted]);

  const submit = async () => {
    if (parsed.length === 0) return;
    setBusy(true);
    setResult(null);
    try {
      const r = await api.bulkClientCreate(parsed);
      setResult({ created: r.created.length, failures: r.failures });
      if (r.failures.length === 0) {
        await onDone();
      } else {
        // Keep modal open so the user can see which rows failed.
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="ייבוא לקוחות מ־CSV"
      size="lg"
    >
      <Stack>
        <Text size="sm" c="dimmed">
          הדבק שורות בפורמט <Code>שם,טלפון,אימייל</Code>. הטלפון אופציונלי;
          האימייל משמש לשליחת קוד האימות והתזכורות. ניתן להעתיק עמודות
          מ־Excel ישירות.
        </Text>
        <Code block dir="ltr">
          דניאל לוי,+972527654321{"\n"}
          מאיה כהן,+972527654322{"\n"}
          יוסי אברהם
        </Code>
        <Textarea
          autosize
          minRows={6}
          maxRows={20}
          placeholder="שורה אחת לכל לקוח…"
          value={pasted}
          onChange={(e) => setPasted(e.currentTarget.value)}
          dir="rtl"
        />
        {parsed.length > 0 && (
          <Text size="sm" c="dimmed">
            זוהו {parsed.length} שורות.
          </Text>
        )}
        {result && (
          <Alert color={result.failures.length === 0 ? "green" : "yellow"}>
            נוצרו {result.created} לקוחות.
            {result.failures.length > 0 && (
              <>
                {" "}
                {result.failures.length} שורות נכשלו:
                <ul>
                  {result.failures.slice(0, 5).map((f, i) => (
                    <li key={i}>
                      <Code>{f.name}</Code>: {f.reason}
                    </li>
                  ))}
                </ul>
              </>
            )}
          </Alert>
        )}
        <Group justify="flex-end">
          <Button variant="subtle" onClick={onClose}>
            סגור
          </Button>
          <Button onClick={submit} loading={busy} disabled={parsed.length === 0}>
            ייבא {parsed.length > 0 ? `${parsed.length} ` : ""}לקוחות
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

function ClientFormModal({
  opened,
  client,
  onClose,
  onSaved,
}: {
  opened: boolean;
  client?: ClientResponse;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}) {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset form fields when the modal opens/closes or client changes.
  useEffect(() => {
    setName(client?.name ?? "");
    setPhone(client?.phone_e164 ?? "");
    setEmail(client?.email ?? "");
    setError(null);
  }, [opened, client]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (client) {
        await api.clientUpdate(client.client_id, {
          name: name.trim(),
          phone_e164: phone.trim() || null,
          email: email.trim() || null,
        });
      } else {
        await api.clientCreate(
          name.trim(),
          phone.trim() || null,
          email.trim() || null,
        );
      }
      await onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : "שגיאה לא צפויה");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={client ? "ערוך לקוח" : "לקוח חדש"}
      size="md"
      // Anchor inside the parent screen's div so display:none on tab switch
      // hides the modal too. Default portals to document.body which keeps
      // the modal visible after the user navigates to another tab.
      withinPortal={false}
    >
      <form onSubmit={handleSubmit}>
        <Stack>
          <TextInput
            label="שם"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            autoFocus
          />
          <TextInput
            label="טלפון (E.164)"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+972501234567"
            dir="ltr"
          />
          <TextInput
            label="אימייל"
            description="קוד האימות ותזכורות יישלחו לכתובת זו"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="client@example.com"
            dir="ltr"
          />
          {error && <Alert color="red">{error}</Alert>}
          <Group justify="flex-end">
            <Button variant="subtle" onClick={onClose}>
              ביטול
            </Button>
            <Button type="submit" loading={busy} disabled={!name.trim()}>
              שמור
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
